import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { firstContentImage } from '../../utils/lotImages';

/**
 * ADMIN Lot Stats — per-lot demand detail for one sale. AdminRoute-gated (NO consignor):
 * this page renders the high bidder's identity.
 *
 * High-bidder NAME is a JOIN, by design: the public auction cache no longer carries
 * currentBidderName (privacy fix) — it carries currentBidderNumber + currentBidder (uid). We
 * resolve the name from the winning bid, falling back to users/{uid}.displayName. That works
 * whether or not the currentBidderName strip has been committed, so cache state doesn't matter
 * here. Views/watches are NOT rendered — no data backs them.
 */

const money = (cents?: number | null) => `$${(((cents ?? 0)) / 100).toFixed(2)}`;
// Staff view: fall back to the barcode frame rather than an empty box when a lot has no
// content photo — seeing something beats seeing nothing when you're working the sale.
const tileImage = (images?: string[]) => firstContentImage(images, { fallbackToAny: true });

interface Row {
  id: string;
  lotNumber: string;
  title: string;
  image: string;
  currentBid: number;
  bidCount: number;
  highBidderNumber: string;
  highBidderName: string;
}

type SortKey = 'lotNumber' | 'currentBid' | 'bidCount';

const LotStats: React.FC = () => {
  const { auctionId } = useParams<{ auctionId: string }>();
  const [rows, setRows] = useState<Row[]>([]);
  const [title, setTitle] = useState('');
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('lotNumber');
  const [sortDesc, setSortDesc] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!auctionId) return;
      try {
        const [aSnap, bidSnap] = await Promise.all([
          getDoc(doc(db, 'auctions', auctionId)),
          getDocs(query(collection(db, 'bids'), where('auctionId', '==', auctionId))),
        ]);
        if (!active) return;

        const a: any = aSnap.exists() ? aSnap.data() : {};
        const lots: any[] = a.lots || [];
        setTitle(a.title || auctionId);

        // Winning bid per lot (voided excluded) → name/number without touching the cache.
        const winningByLot = new Map<string, any>();
        const countByLot = new Map<string, number>();
        bidSnap.forEach((d) => {
          const b: any = d.data();
          if (b.status === 'rejected' || b.status === 'pending' || b.voidReason) return;
          countByLot.set(b.lotId, (countByLot.get(b.lotId) ?? 0) + 1);
          if (b.isWinning === true) {
            const cur = winningByLot.get(b.lotId);
            if (!cur || (b.amount ?? 0) > (cur.amount ?? 0)) winningByLot.set(b.lotId, b);
          }
        });

        // Resolve display names for the distinct winning bidders (fallback when the bid doc
        // has no bidderName — e.g. engine-placed proxy-auto bids).
        const needUid = new Set<string>();
        winningByLot.forEach((b) => { if (!b.bidderName && b.bidderId) needUid.add(b.bidderId); });
        const nameByUid = new Map<string, string>();
        await Promise.all(Array.from(needUid).map(async (uid) => {
          try {
            const u = await getDoc(doc(db, 'users', uid));
            if (u.exists()) nameByUid.set(uid, (u.data() as any).displayName || '');
          } catch { /* best effort */ }
        }));
        if (!active) return;

        setRows(lots.map((l) => {
          const win = winningByLot.get(l.id);
          return {
            id: l.id,
            lotNumber: String(l.lotNumber ?? ''),
            title: l.title || '(untitled)',
            image: tileImage(l.images),
            currentBid: l.currentBid ?? l.startingBid ?? 0,
            bidCount: countByLot.get(l.id) ?? l.bidCount ?? 0,
            highBidderNumber: win?.bidderNumber || l.currentBidderNumber || '',
            highBidderName: win?.bidderName || (win?.bidderId ? nameByUid.get(win.bidderId) || '' : ''),
          };
        }));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [auctionId]);

  const visible = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const list = q
      ? rows.filter((r) => r.lotNumber.toLowerCase().includes(q) || r.title.toLowerCase().includes(q))
      : rows;
    const sorted = [...list].sort((a, b) => {
      if (sortKey === 'lotNumber') {
        const an = parseInt(a.lotNumber, 10); const bn = parseInt(b.lotNumber, 10);
        if (!isNaN(an) && !isNaN(bn)) return an - bn;
        return a.lotNumber.localeCompare(b.lotNumber);
      }
      return (a[sortKey] as number) - (b[sortKey] as number);
    });
    return sortDesc ? sorted.reverse() : sorted;
  }, [rows, filter, sortKey, sortDesc]);

  const setSort = (k: SortKey) => {
    if (k === sortKey) setSortDesc((d) => !d);
    else { setSortKey(k); setSortDesc(false); }
  };
  const arrow = (k: SortKey) => (sortKey === k ? (sortDesc ? ' ▼' : ' ▲') : '');

  if (loading) return <div className="text-center py-12 text-gray-600">Loading lot stats…</div>;

  const withBids = rows.filter((r) => r.bidCount > 0).length;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold">Lot Stats</h1>
          <p className="text-gray-600">{title} · {rows.length} lots · {withBids} with bids</p>
        </div>
        <div className="flex gap-2">
          <input
            className="input py-2"
            placeholder="Filter lot # / title"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <Link to={`/admin/sale/${auctionId}/dashboard`} className="btn-secondary">Dashboard</Link>
          <Link to={`/admin/sale/${auctionId}/registrations`} className="btn-secondary">Registrations</Link>
        </div>
      </div>

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b bg-gray-50">
              <th className="py-2 px-2 w-16"></th>
              <th className="py-2 px-2 cursor-pointer" onClick={() => setSort('lotNumber')}>Lot{arrow('lotNumber')}</th>
              <th className="py-2 px-2">Title</th>
              <th className="py-2 px-2 text-right cursor-pointer" onClick={() => setSort('currentBid')}>Current High{arrow('currentBid')}</th>
              <th className="py-2 px-2">High Bidder</th>
              <th className="py-2 px-2 text-right cursor-pointer" onClick={() => setSort('bidCount')}>Bids{arrow('bidCount')}</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((r) => (
              <tr key={r.id} className="border-b last:border-0 hover:bg-gray-50">
                <td className="py-2 px-2">
                  {r.image
                    ? <img src={r.image} alt="" className="w-12 h-12 object-cover rounded" loading="lazy" />
                    : <div className="w-12 h-12 bg-gray-100 rounded" />}
                </td>
                <td className="py-2 px-2 font-mono">{r.lotNumber}</td>
                <td className="py-2 px-2">
                  <Link to={`/auctions/${auctionId}/lots/${r.id}`} className="font-semibold text-gray-800 hover:text-primary-600">
                    {r.title}
                  </Link>
                </td>
                <td className="py-2 px-2 text-right font-bold">{money(r.currentBid)}</td>
                <td className="py-2 px-2">
                  {r.bidCount > 0
                    ? <span>{r.highBidderNumber ? `#${r.highBidderNumber}` : '—'}{r.highBidderName ? ` · ${r.highBidderName}` : ''}</span>
                    : <span className="text-gray-400">—</span>}
                </td>
                <td className="py-2 px-2 text-right font-semibold">{r.bidCount}</td>
              </tr>
            ))}
            {visible.length === 0 && (
              <tr><td colSpan={6} className="py-6 text-center text-gray-500">No lots match.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-gray-500">Views and watches are not shown: nothing in the schema tracks them yet.</p>
    </div>
  );
};

export default LotStats;
